import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'];

const empty = { label:'Home', first_name:'',last_name:'',phone:'',address:'',address2:'',city:'',state:'',zip:'' };

export default function Addresses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    load();
  }, [user]);

  const load = () => api.get('/addresses').then(r => setAddresses(r.data)).catch(() => {});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ label:a.label,first_name:a.first_name,last_name:a.last_name,phone:a.phone||'',address:a.address,address2:a.address2||'',city:a.city,state:a.state,zip:a.zip }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/addresses/${editing.id}`, form);
      else await api.post('/addresses', form);
      await load();
      setShowForm(false);
    } finally { setSaving(false); }
  };

  const setDefault = async (id) => { await api.put(`/addresses/${id}/default`); load(); };
  const del = async (id) => { if (window.confirm('Delete this address?')) { await api.delete(`/addresses/${id}`); load(); } };

  const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };

  return (
    <div className="container" style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 20, fontSize: 14 }}><Link to="/orders">&larr; Back to Account</Link></div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 24 }}>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Saved Addresses</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Address</button>
      </div>

      {addresses.length === 0 && !showForm && (
        <div className="empty-state">
          <div className="icon">&#128205;</div>
          <h3>No saved addresses</h3>
          <p>Add an address to speed up future checkouts.</p>
          <button className="btn btn-primary" style={{ marginTop:16 }} onClick={openAdd}>Add Your First Address</button>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom: showForm ? 24 : 0 }}>
        {addresses.map(a => (
          <div key={a.id} style={{ border: `2px solid ${a.is_default ? '#4f46e5':'#e5e7eb'}`, borderRadius:12, padding:'16px 20px', background: a.is_default ? '#f5f3ff':'white' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>
                  {a.label}
                  {a.is_default && <span style={{ marginLeft:8, fontSize:11, background:'#4f46e5', color:'white', borderRadius:4, padding:'2px 8px' }}>Default</span>}
                </div>
                <div style={{ fontSize:14, color:'#374151', lineHeight:1.6 }}>
                  {a.first_name} {a.last_name}<br/>
                  {a.address}{a.address2 ? `, ${a.address2}` : ''}<br/>
                  {a.city}, {a.state} {a.zip}
                  {a.phone && <><br/>{a.phone}</>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                {!a.is_default && <button className="btn btn-outline btn-sm" onClick={() => setDefault(a.id)}>Set Default</button>}
                <button className="btn btn-outline btn-sm" onClick={() => openEdit(a)}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => del(a.id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:12, padding:24 }}>
          <h3 style={{ marginBottom:20, fontSize:18 }}>{editing ? 'Edit Address' : 'Add New Address'}</h3>
          <form onSubmit={handleSave}>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>Label</label>
              <select value={form.label} onChange={e => set('label', e.target.value)} style={{ ...inp, background:'white' }}>
                {['Home','Work','Other'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>First Name *</label>
                <input style={inp} required value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="John" />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>Last Name *</label>
                <input style={inp} required value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Doe" />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>Phone</label>
                <input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 000-0000" />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>Address *</label>
                <input style={inp} required value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>Apt/Suite (optional)</label>
                <input style={inp} value={form.address2} onChange={e => set('address2', e.target.value)} placeholder="Apt 4B" />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>City *</label>
                <input style={inp} required value={form.city} onChange={e => set('city', e.target.value)} placeholder="New York" />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>State *</label>
                  <select required style={{ ...inp, background:'white' }} value={form.state} onChange={e => set('state', e.target.value)}>
                    <option value="">Select...</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:5 }}>ZIP *</label>
                  <input style={inp} required value={form.zip} onChange={e => set('zip', e.target.value.replace(/[^\d-]/g,'').slice(0,10))} placeholder="10001" />
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:12, marginTop:20 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Address'}</button>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
